import * as XLSX from 'xlsx';
import {saveAs} from 'file-saver';
import {
  ChecklistFormValues,
  ChecklistListItemFormValues,
  ChecklistSidebarSelection,
  createDefaultChecklist,
  DepartmentFormValues,
} from '../../../types';

export const CHECKLIST_EXCEL_HEADERS = {
  id: "מזהה צ'קליסט",
  status: "צ'קליסט פעיל",
  title: "כותרת צ'קליסט",
  departments: 'משוייך למחלקה/ות',
  description: 'הוספת תיאור',
  showInBrief: 'הצגה בבריף',
  approvalRequired: 'חובת אישור',
  enableComments: 'אפשור הוספת תגובות',
  resetDaily: 'איפוס יומי',
} as const;

export type ChecklistExcelColumnKey = keyof typeof CHECKLIST_EXCEL_HEADERS;

export const CHECKLIST_EXCEL_COLUMNS: Array<{
  key: ChecklistExcelColumnKey;
  header: string;
}> = [
  {key: 'id', header: CHECKLIST_EXCEL_HEADERS.id},
  {key: 'status', header: CHECKLIST_EXCEL_HEADERS.status},
  {key: 'title', header: CHECKLIST_EXCEL_HEADERS.title},
  {key: 'departments', header: CHECKLIST_EXCEL_HEADERS.departments},
  {key: 'description', header: CHECKLIST_EXCEL_HEADERS.description},
  {key: 'showInBrief', header: CHECKLIST_EXCEL_HEADERS.showInBrief},
  {key: 'approvalRequired', header: CHECKLIST_EXCEL_HEADERS.approvalRequired},
  {key: 'enableComments', header: CHECKLIST_EXCEL_HEADERS.enableComments},
  {key: 'resetDaily', header: CHECKLIST_EXCEL_HEADERS.resetDaily},
];

// Instructions live in their own sheet, one row per instruction, linked back
// to a checklist via the shared "id" column — this replaces the old
// comma-joined listInstructions/listStatus columns so a single instruction
// can contain commas/long text without corrupting the parse, and so the
// sheet itself reads as a real list.
export const CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS = {
  checklistId: "מזהה צ'קליסט",
  instruction: 'הוראה',
  status: 'סטטוס פריט',
} as const;

export type ChecklistInstructionsExcelColumnKey =
  keyof typeof CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS;

export const CHECKLIST_INSTRUCTIONS_EXCEL_COLUMNS: Array<{
  key: ChecklistInstructionsExcelColumnKey;
  header: string;
  width: number;
}> = [
  {
    key: 'checklistId',
    header: CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.checklistId,
    width: 14,
  },
  {
    key: 'instruction',
    header: CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.instruction,
    width: 45,
  },
  {key: 'status', header: CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.status, width: 14},
];

const SAMPLE_CHECKLIST_ID = '1';

export const CHECKLIST_TEMPLATE_SAMPLE_ROW: Record<string, string> = {
  [CHECKLIST_EXCEL_HEADERS.id]: SAMPLE_CHECKLIST_ID,
  [CHECKLIST_EXCEL_HEADERS.status]: 'כן',
  [CHECKLIST_EXCEL_HEADERS.title]: 'צ’קליסט לדוגמה',
  [CHECKLIST_EXCEL_HEADERS.departments]: 'כל המחלקות',
  [CHECKLIST_EXCEL_HEADERS.description]: 'תיאור לדוגמה',
  [CHECKLIST_EXCEL_HEADERS.showInBrief]: 'לא',
  [CHECKLIST_EXCEL_HEADERS.approvalRequired]: 'לא',
  [CHECKLIST_EXCEL_HEADERS.enableComments]: 'לא',
  [CHECKLIST_EXCEL_HEADERS.resetDaily]: 'כן',
};

export const CHECKLIST_INSTRUCTIONS_TEMPLATE_SAMPLE_ROWS: Record<
  string,
  string
>[] = [
  {
    [CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.checklistId]: SAMPLE_CHECKLIST_ID,
    [CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.instruction]: 'בדיקת ניקיון',
    [CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.status]: 'כן',
  },
  {
    [CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.checklistId]: SAMPLE_CHECKLIST_ID,
    [CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.instruction]: 'בדיקת מלאי',
    [CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.status]: 'כן',
  },
];

const TEMPLATE_METADATA_SHEET_NAME = "פרטי צ'קליסטים";
const TEMPLATE_INSTRUCTIONS_SHEET_NAME = 'הוראות';
const ALL_DEPARTMENTS_LABEL = 'כל המחלקות';

function booleanToExcel(value: boolean): string {
  return value ? 'כן' : 'לא';
}

export function mapChecklistToExcelRows(
  checklist: ChecklistFormValues,
  nodeDepartments: DepartmentFormValues[],
  checklistId: string,
): {
  metadataRow: Record<string, string>;
  instructionRows: Record<string, string>[];
} {
  const departmentsCell =
    checklist.departments[0] === 'all'
      ? ALL_DEPARTMENTS_LABEL
      : checklist.departments
          .map(id => nodeDepartments.find(d => d.id === id)?.name?.trim())
          .filter((name): name is string => Boolean(name))
          .join(', ');

  const listItems = checklist.list.filter(
    item => (item.instruction ?? '').trim().length > 0,
  );

  const metadataRow: Record<string, string> = {
    [CHECKLIST_EXCEL_HEADERS.id]: checklistId,
    [CHECKLIST_EXCEL_HEADERS.status]: booleanToExcel(checklist.status),
    [CHECKLIST_EXCEL_HEADERS.title]: checklist.title,
    [CHECKLIST_EXCEL_HEADERS.departments]: departmentsCell,
    [CHECKLIST_EXCEL_HEADERS.description]: checklist.description?.trim() ?? '',
    [CHECKLIST_EXCEL_HEADERS.showInBrief]: booleanToExcel(
      checklist.showInBrief,
    ),
    [CHECKLIST_EXCEL_HEADERS.approvalRequired]: booleanToExcel(
      checklist.approvalRequired,
    ),
    [CHECKLIST_EXCEL_HEADERS.enableComments]: booleanToExcel(
      checklist.enableComments,
    ),
    [CHECKLIST_EXCEL_HEADERS.resetDaily]: booleanToExcel(checklist.resetDaily),
  };

  const instructionRows: Record<string, string>[] = listItems.map(item => ({
    [CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.checklistId]: checklistId,
    [CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.instruction]:
      item.instruction?.trim() ?? '',
    [CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.status]: booleanToExcel(
      item.status,
    ),
  }));

  return {metadataRow, instructionRows};
}

function downloadChecklistsExcelFile(
  metadataRows: Record<string, string>[],
  instructionRows: Record<string, string>[],
  fileName: string,
) {
  const metadataSheet = XLSX.utils.json_to_sheet(metadataRows);
  (metadataSheet as XLSX.WorkSheet)['!cols'] = CHECKLIST_EXCEL_COLUMNS.map(
    c => ({wch: Math.max(c.header.length, 14)}),
  );

  const instructionsSheet = XLSX.utils.json_to_sheet(instructionRows);
  (instructionsSheet as XLSX.WorkSheet)['!cols'] =
    CHECKLIST_INSTRUCTIONS_EXCEL_COLUMNS.map(c => ({
      wch: Math.max(c.header.length, c.width),
    }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, metadataSheet, TEMPLATE_METADATA_SHEET_NAME);
  XLSX.utils.book_append_sheet(
    wb,
    instructionsSheet,
    TEMPLATE_INSTRUCTIONS_SHEET_NAME,
  );

  const buf = XLSX.write(wb, {bookType: 'xlsx', type: 'array'});
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });
  saveAs(blob, fileName);
}

function sanitizeFileNamePart(value: string): string {
  return (
    value.replace(/[^\w֐-׿-]+/g, '_').replace(/^_|_$/g, '') ||
    'Checklist'
  );
}

export function downloadOnboardingChecklistExcelTemplate(options?: {
  fileName?: string;
  nodeName?: string;
}) {
  const namePart = options?.nodeName
    ? sanitizeFileNamePart(options.nodeName)
    : 'Checklist';

  downloadChecklistsExcelFile(
    [CHECKLIST_TEMPLATE_SAMPLE_ROW],
    CHECKLIST_INSTRUCTIONS_TEMPLATE_SAMPLE_ROWS,
    options?.fileName ?? `Checklist_Template_${namePart}.xlsx`,
  );
}

export function downloadOnboardingChecklistExcelExport(
  checklist: ChecklistFormValues,
  nodeDepartments: DepartmentFormValues[],
  options?: {fileName?: string},
) {
  const {metadataRow, instructionRows} = mapChecklistToExcelRows(
    checklist,
    nodeDepartments,
    SAMPLE_CHECKLIST_ID,
  );
  const titlePart = sanitizeFileNamePart(checklist.title);

  downloadChecklistsExcelFile(
    [metadataRow],
    instructionRows,
    options?.fileName ?? `Checklist_${titlePart}.xlsx`,
  );
}

export function parseExcelSheet(ws: XLSX.WorkSheet): Record<string, unknown>[] {
  const rows = XLSX.utils.sheet_to_json(ws, {
    header: 1,
    defval: '',
  }) as unknown[][];

  const nonEmptyRows = rows.filter(r => r.some(c => String(c).trim() !== ''));
  if (!nonEmptyRows.length) return [];

  const junkFirstRow = nonEmptyRows[0].every(
    (c, i) =>
      (i === 0 && String(c).trim().toLowerCase().includes('table')) ||
      String(c).trim() === '',
  );
  const headerRow = junkFirstRow ? (nonEmptyRows[1] ?? []) : nonEmptyRows[0];
  const dataRows = junkFirstRow ? nonEmptyRows.slice(2) : nonEmptyRows.slice(1);

  const rawHeaders = headerRow.map(h => String(h).trim());
  const keepIdx: number[] = [];
  const headers = rawHeaders
    .map((h, i) => {
      if (h) keepIdx.push(i);
      return h;
    })
    .filter(Boolean);

  return dataRows.map(r => {
    const obj: Record<string, unknown> = {};
    keepIdx.forEach((colIdx, j) => {
      obj[headers[j]] = r[colIdx] ?? '';
    });
    return obj;
  });
}

export function parseBooleanFromExcel(
  val: unknown,
  defaultValue = false,
): boolean {
  if (val === '' || val === null || val === undefined) return defaultValue;
  if (typeof val === 'boolean') return val;
  if (typeof val === 'number') return val === 1;
  if (typeof val === 'string') {
    const lower = val.toLowerCase().trim();
    if (
      lower === 'false' ||
      lower === 'לא' ||
      lower === '0' ||
      lower === 'inactive'
    ) {
      return false;
    }
    return (
      lower === 'true' || lower === 'כן' || lower === '1' || lower === 'active'
    );
  }
  return defaultValue;
}

export function parseCommaSeparated(val: unknown): string[] {
  if (!val) return [];
  return String(val)
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
}

function getNamedOrIndexedWorksheet(
  workbook: XLSX.WorkBook,
  sheetName: string,
  fallbackIndex: number,
): XLSX.WorkSheet | null {
  const name =
    workbook.SheetNames.find(n => n === sheetName) ??
    workbook.SheetNames[fallbackIndex] ??
    workbook.SheetNames[0];
  if (!name) return null;
  return workbook.Sheets[name] ?? null;
}

function getMetadataWorksheet(workbook: XLSX.WorkBook): XLSX.WorkSheet | null {
  return getNamedOrIndexedWorksheet(workbook, TEMPLATE_METADATA_SHEET_NAME, 0);
}

function getInstructionsWorksheet(
  workbook: XLSX.WorkBook,
): XLSX.WorkSheet | null {
  return getNamedOrIndexedWorksheet(
    workbook,
    TEMPLATE_INSTRUCTIONS_SHEET_NAME,
    1,
  );
}

export function isValidOnboardingChecklistExcelData(
  metadataRows: Record<string, unknown>[],
  instructionRows: Record<string, unknown>[],
): boolean {
  if (!metadataRows.length) return false;
  const metaKeys = Object.keys(metadataRows[0]);
  const hasValidMetadata =
    metaKeys.includes(CHECKLIST_EXCEL_HEADERS.id) &&
    metaKeys.includes(CHECKLIST_EXCEL_HEADERS.title) &&
    metaKeys.includes(CHECKLIST_EXCEL_HEADERS.departments);
  if (!hasValidMetadata) return false;

  if (!instructionRows.length) return true;
  const instructionKeys = Object.keys(instructionRows[0]);
  return (
    instructionKeys.includes(CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.checklistId) &&
    instructionKeys.includes(CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.instruction)
  );
}

function isTemplateSampleRow(row: Record<string, unknown>): boolean {
  const title = String(row[CHECKLIST_EXCEL_HEADERS.title] ?? '').trim();
  return title === CHECKLIST_TEMPLATE_SAMPLE_ROW[CHECKLIST_EXCEL_HEADERS.title];
}

export function matchDepartmentNamesToIds(
  names: string[],
  nodeDepartments: DepartmentFormValues[],
): {departments: string[]} | {error: string} {
  if (names.some(n => n.trim() === ALL_DEPARTMENTS_LABEL)) {
    return {departments: ['all']};
  }

  const ids: string[] = [];
  const unmatched: string[] = [];

  names.forEach(name => {
    const dept = nodeDepartments.find(
      d => d.name.toLowerCase().trim() === name.toLowerCase().trim(),
    );
    if (dept) {
      ids.push(dept.id);
    } else {
      unmatched.push(name);
    }
  });

  if (unmatched.length > 0) {
    return {
      error: `מחלקות לא נמצאו: ${unmatched.join(', ')}`,
    };
  }

  if (ids.length === 0) {
    return {error: 'יש לציין לפחות מחלקה אחת'};
  }

  return {departments: ids};
}

function buildListFromInstructionRows(
  rows: Record<string, unknown>[],
): ChecklistListItemFormValues[] {
  return rows
    .map(row => ({
      instruction: String(
        row[CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.instruction] ?? '',
      ).trim(),
      status: parseBooleanFromExcel(
        row[CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.status],
        true,
      ),
    }))
    .filter(item => item.instruction.length > 0);
}

function getScopeFields(
  sidebarSelection: ChecklistSidebarSelection,
): Pick<ChecklistFormValues, 'shiftId' | 'folderId'> {
  return sidebarSelection.type === 'shift'
    ? {shiftId: sidebarSelection.id, folderId: undefined}
    : {folderId: sidebarSelection.id, shiftId: undefined};
}

export function mapExcelRowToChecklist(
  row: Record<string, unknown>,
  instructionRows: Record<string, unknown>[],
  nodeDepartments: DepartmentFormValues[],
  sidebarSelection: ChecklistSidebarSelection,
): {checklist: ChecklistFormValues} | {error: string} {
  const title = String(row[CHECKLIST_EXCEL_HEADERS.title] ?? '').trim();
  if (title.length < 3) {
    return {error: 'כותרת צ’קליסט חייבת להכיל לפחות 3 תווים'};
  }

  const departmentsRaw = String(
    row[CHECKLIST_EXCEL_HEADERS.departments] ?? '',
  ).trim();
  if (!departmentsRaw) {
    return {error: 'חסר שיוך למחלקה/ות'};
  }

  const departmentNames = parseCommaSeparated(departmentsRaw);
  const deptMatch = matchDepartmentNamesToIds(departmentNames, nodeDepartments);
  if ('error' in deptMatch) {
    return {error: deptMatch.error};
  }

  const list = buildListFromInstructionRows(instructionRows);
  if (list.length === 0) {
    return {
      error: `נדרש לפחות הוראה אחת בגיליון "${TEMPLATE_INSTRUCTIONS_SHEET_NAME}" עם המזהה של הצ'קליסט`,
    };
  }

  const checklist = createDefaultChecklist({
    title,
    description: String(row[CHECKLIST_EXCEL_HEADERS.description] ?? '').trim(),
    status: parseBooleanFromExcel(row[CHECKLIST_EXCEL_HEADERS.status], true),
    departments: deptMatch.departments,
    showInBrief: parseBooleanFromExcel(
      row[CHECKLIST_EXCEL_HEADERS.showInBrief],
      false,
    ),
    approvalRequired: parseBooleanFromExcel(
      row[CHECKLIST_EXCEL_HEADERS.approvalRequired],
      false,
    ),
    enableComments: parseBooleanFromExcel(
      row[CHECKLIST_EXCEL_HEADERS.enableComments],
      false,
    ),
    resetDaily: parseBooleanFromExcel(
      row[CHECKLIST_EXCEL_HEADERS.resetDaily],
      true,
    ),
    list,
    ...getScopeFields(sidebarSelection),
  });

  return {checklist};
}

export type ParseOnboardingChecklistExcelResult = {
  checklists: ChecklistFormValues[];
  rowErrors: string[];
  skippedSampleRows: number;
};

export function parseOnboardingChecklistExcelWorkbook(
  workbook: XLSX.WorkBook,
  nodeDepartments: DepartmentFormValues[],
  sidebarSelection: ChecklistSidebarSelection,
): ParseOnboardingChecklistExcelResult {
  const metadataSheet = getMetadataWorksheet(workbook);
  if (!metadataSheet) {
    return {
      checklists: [],
      rowErrors: ['הקובץ ריק או לא תקין'],
      skippedSampleRows: 0,
    };
  }

  const instructionsSheet = getInstructionsWorksheet(workbook);
  const metadataRows = parseExcelSheet(metadataSheet);
  const instructionRows = instructionsSheet
    ? parseExcelSheet(instructionsSheet)
    : [];

  if (!isValidOnboardingChecklistExcelData(metadataRows, instructionRows)) {
    return {
      checklists: [],
      rowErrors: ['פורמט קובץ לא תקין. השתמשו בתבנית שהורדה מהמערכת'],
      skippedSampleRows: 0,
    };
  }

  const instructionRowsByChecklistId = new Map<
    string,
    Record<string, unknown>[]
  >();
  instructionRows.forEach(row => {
    const checklistId = String(
      row[CHECKLIST_INSTRUCTIONS_EXCEL_HEADERS.checklistId] ?? '',
    ).trim();
    if (!checklistId) return;

    const group = instructionRowsByChecklistId.get(checklistId) ?? [];
    group.push(row);
    instructionRowsByChecklistId.set(checklistId, group);
  });

  const checklists: ChecklistFormValues[] = [];
  const rowErrors: string[] = [];
  let skippedSampleRows = 0;

  metadataRows.forEach((row, index) => {
    const title = String(row[CHECKLIST_EXCEL_HEADERS.title] ?? '').trim();
    if (!title) return;

    if (isTemplateSampleRow(row)) {
      skippedSampleRows += 1;
      return;
    }

    const checklistId = String(row[CHECKLIST_EXCEL_HEADERS.id] ?? '').trim();
    const relatedInstructionRows =
      instructionRowsByChecklistId.get(checklistId) ?? [];

    const result = mapExcelRowToChecklist(
      row,
      relatedInstructionRows,
      nodeDepartments,
      sidebarSelection,
    );
    if ('error' in result) {
      const rowLabel = title || `שורה ${index + 2}`;
      rowErrors.push(`${rowLabel}: ${result.error}`);
      return;
    }

    checklists.push(result.checklist);
  });

  return {checklists, rowErrors, skippedSampleRows};
}

export async function readOnboardingChecklistExcelFile(
  file: File,
  nodeDepartments: DepartmentFormValues[],
  sidebarSelection: ChecklistSidebarSelection,
): Promise<ParseOnboardingChecklistExcelResult> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(new Uint8Array(buffer), {type: 'array'});
  return parseOnboardingChecklistExcelWorkbook(
    workbook,
    nodeDepartments,
    sidebarSelection,
  );
}
